/**
 * Canonical internal-link scheme: `pjas://<type>:<info>` (e.g. `pjas://order:22`).
 *
 * A `pjas://` link is purely a string convention that maps 1:1 to the in-app jumper path
 * `/jump/<type>/<info>` (see `EntityRedirectPage.vue`), which performs the real route
 * resolution. `info` may itself contain `:`/`.` (e.g. `pjas://vbudget:5:10`), so parsing only
 * splits on the first colon.
 */

// the entity types the jumper (`/jump/:type/:info?`) knows how to resolve.
export const JUMP_TYPES = [
  'order',
  'company',
  'account',
  'vbudget',
  'poolmirror',
  'candidate',
  'ejm_job',
  'promptsuite_app',
] as const;

export type JumpType = (typeof JUMP_TYPES)[number];

const PJAS_SCHEME = 'pjas://';

function isJumpType(type: string): type is JumpType {
  return (JUMP_TYPES as readonly string[]).includes(type);
}

/**
 * Builds a canonical link, e.g. `buildPjasUri('order', 22)` -> `"pjas://order:22"`. For
 * param-less list/screen types pass no info: `buildPjasUri('order')` -> `"pjas://order"`.
 */
export function buildPjasUri(type: JumpType, info?: string | number): string {
  return info === undefined || info === '' ? `${PJAS_SCHEME}${type}` : `${PJAS_SCHEME}${type}:${info}`;
}

/**
 * Parses a `pjas://` link into its type + info. Splits on the first colon (info is optional for
 * param-less types). Returns null for non-`pjas://` strings or unknown types.
 */
export function parsePjasUri(uri: string): { type: JumpType; info: string } | null {
  if (!uri.startsWith(PJAS_SCHEME)) return null;
  const rest = uri.slice(PJAS_SCHEME.length).replace(/\/+$/, '');
  if (!rest) return null;
  const idx = rest.indexOf(':');
  const type = idx === -1 ? rest : rest.slice(0, idx);
  const info = idx === -1 ? '' : rest.slice(idx + 1);
  if (!isJumpType(type)) return null;
  // a real link's info is path-like (an id, optionally with `:`/`/` segments). a query string,
  // fragment or whitespace means a fabricated/malformed link — reject it so callers fail loudly
  // instead of navigating to an unresolvable jump path.
  if (/[?#\s]/.test(info)) return null;
  return { type, info };
}

/** Resolves a `pjas://` link to the in-app jumper path it maps to, or null if invalid. */
export function pjasUriToJumpPath(uri: string): string | null {
  const parsed = parsePjasUri(uri);
  if (!parsed) return null;
  return parsed.info ? `/jump/${parsed.type}/${parsed.info}` : `/jump/${parsed.type}`;
}
